import katex from 'katex';
import xss from 'xss';

import { groupThousandsInKatexHtml } from './groupThousandsInKatexHtml';
import { KATEX_MACROS, toKatexLatex } from './katexMacros';

// `\htmlData` is the one trusted command: it only adds `data-*` attributes to a span, and it is how the
// Studio preview of a slotted Math blank names each slot for a page-scanning agent (useMathBlankPreview).
const KATEX_OPTIONS = {
  throwOnError: false,
  macros: KATEX_MACROS,
  trust: ({ command }: { command: string }) => command === '\\htmlData'
};

// A digit run that carries a script (`1000^{2}`, `1200^\circ`): KaTeX attaches the script to the LAST digit
// only and emits the rest as its own text (`100` + `0²`), so the grouping pass never sees one number and the
// editors' `1,000²` would show as `1000²`. Bracing the run keeps it one number and typesets the same. A run
// right after a letter or `\` is a command's argument (`\frac12^2`) and is left alone. A captured prefix
// rather than a lookbehind, which the es2015 target rejects.
const SCRIPTED_NUMBER = /(^|[^\dA-Za-z.\\])(\d{4,})(?=\s*[\^_])/g;

const braceScriptedNumbers = (latex: string) =>
  latex.replace(SCRIPTED_NUMBER, (_, prefix: string, digits: string) => `${prefix}{${digits}}`);

/**
 * The single LaTeX → display-HTML path for static (non-editable) math: normalize mathlive's LaTeX
 * for KaTeX, typeset it, and draw the thousands separators the editors also paint. On a
 * typesetting failure the raw LaTeX is shown instead — sanitized, since it is authored content
 * injected as HTML, and ungrouped, exactly as stored. The separator pass is cosmetic, so its
 * failure only costs the grouping, never the typeset formula.
 */
export function renderStaticMathDisplayHtml(latex: string): string {
  let html: string;
  try {
    html = katex.renderToString(braceScriptedNumbers(toKatexLatex(latex)) || '\\square', KATEX_OPTIONS);
  } catch {
    return xss(latex);
  }
  try {
    return groupThousandsInKatexHtml(html);
  } catch {
    return html;
  }
}
